<template>
  <form @submit.prevent="onSubmit">
    <confirm-dialog ref="confirmDialog"></confirm-dialog>

    <div class="row h-40">
      <p class="bb-page-title mt-3 mb-1">SPELL DETAILS</p>
    </div>

    <div class="row mb-2 mt-0">
      <div class="col-3">
        <div class="row mt-4">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="closeSidePanel"
            >
              Cancel
            </button>
            <button
              v-if="!isNewSpell"
              class="btn btn-primary btn-lg"
              type="button"
              @click="confirmDeletion"
            >
              Delete
            </button>
            <button
              v-if="isNewSpell"
              :disabled="!canSubmit"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <button
              v-if="!isNewSpell"
              :disabled="!canSubmit"
              class="btn btn-primary btn-lg"
              type="button"
              @click="onSubmit"
            >
              OK
            </button>
          </div>
        </div>
      </div>

      <div class="col m-3 bb-tooltip-wrapper">
        <p class="bb-hdg-lg">{{ spellHeading }}</p>

        <div class="row mt-2 mb-2">
          <div
            v-if="!isNewSpell"
            class="col-2"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="rowNumber"
                  id="row"
                  disabled
                />
                <label
                  class="bb-float-label"
                  for="row"
                  >Row</label
                >
              </div>
            </div>
          </div>

          <div :class="isNewSpell ? 'col-4' : 'col-3'">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.type"
                  class="form-control"
                  :class="{ 'is-invalid': errorFields.includes('type') }"
                  id="spell_type"
                  @change="onTypeChange"
                >
                  <option
                    v-for="type in spellTypes"
                    :key="type"
                    :value="type"
                  >
                    {{ type }}
                  </option>
                </select>
                <label
                  class="bb-float-label"
                  for="spell_type"
                  >Spell Type</label
                >
              </div>
            </div>
          </div>

          <div :class="isNewSpell ? 'col-8' : 'col-7'">
            <div class="form-group">
              <div class="form-floating">
                <input
                  v-model.trim="local_spell.name"
                  maxlength="20"
                  class="form-control"
                  :class="{ 'is-invalid': errorFields.includes('name') }"
                  id="spell_name"
                  :required="!local_spell.name"
                  @input="onNameInput"
                />
                <label
                  class="bb-float-label"
                  for="spell_name"
                  >Spell Name</label
                >
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="local_spell.type === 'Rune'"
          class="row mt-2 mb-2"
        >
          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.casting_rune"
                  class="form-control"
                  :class="{
                    'is-invalid': errorFields.includes('casting_rune'),
                  }"
                  id="casting_rune"
                  :required="!local_spell.casting_rune"
                >
                  <option :value="null">Select...</option>
                  <option
                    v-for="rune in runes"
                    :key="rune.id"
                    :value="rune.id"
                  >
                    {{ rune.name }}
                  </option>
                </select>
                <label
                  class="bb-float-label"
                  for="casting_rune"
                  >Casting Rune</label
                >
              </div>
            </div>
          </div>

          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.secondary_rune"
                  class="form-control"
                  id="secondary_rune"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="rune in runes"
                    :key="rune.id"
                    :value="rune.id"
                  >
                    {{ rune.name }}
                  </option>
                </select>
                <label
                  class="bb-float-label optional"
                  for="secondary_rune"
                  >Secondary Rune</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div
            :class="field.class"
            v-for="field in detailSelectFields"
            :key="field.key"
          >
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell[field.key]"
                  class="form-control text-center"
                  :id="field.key"
                >
                  <option
                    v-for="option in field.options"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <label
                  class="bb-float-label"
                  :for="field.key"
                  >{{ field.label }}</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div class="col-3">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.resist"
                  class="form-control text-center"
                  id="resist"
                >
                  <option
                    v-for="option in resistOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <label
                  class="bb-float-label"
                  for="resist"
                  >Resist</label
                >
              </div>
            </div>
          </div>

          <div class="col-4">
            <div class="form-group">
              <CounterEmit
                ref="magCounter"
                :pass_ref="'mag'"
                :pass_label="'Mag'"
                :pass_count="local_spell.mag"
                :pass_min="computeMagMin"
                :pass_max="10"
                :pass_digits="2"
                :invalid="errorFields.includes('mag')"
                @change="onCounterChanged"
              />
            </div>
          </div>

          <div class="col-3">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.area"
                  class="form-control text-center"
                  id="area"
                >
                  <option
                    v-for="option in areaOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <label
                  class="bb-float-label"
                  for="area"
                  >Area</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div class="col-3">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.progressive"
                  class="form-control text-center"
                  id="progressive"
                  @change="onProgressiveChange"
                >
                  <option :value="true">Yes</option>
                  <option :value="false">No</option>
                </select>
                <label
                  class="bb-float-label"
                  for="progressive"
                  >Progressive</label
                >
              </div>
            </div>
          </div>

          <div class="col-3">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.triggered"
                  class="form-control text-center"
                  id="triggered"
                >
                  <option :value="true">Yes</option>
                  <option :value="false">No</option>
                </select>
                <label
                  class="bb-float-label"
                  for="triggered"
                  >Triggered</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <textarea
                  v-model="local_spell.short_description"
                  id="short_description"
                  rows="2"
                  maxlength="250"
                  class="form-control bb-textarea-with-label bb-card-short-description-control bb-static-spell-short-description-control"
                ></textarea>
                <label
                  class="bb-float-label optional"
                  for="short_description"
                  >Short Description</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div class="form-group">
            <div class="form-floating">
              <textarea
                v-model="local_spell.description"
                id="description"
                rows="5"
                maxlength="1024"
                class="form-control bb-textarea-with-label bb-card-description-control bb-static-spell-description-control"
              ></textarea>
              <label
                class="bb-float-label optional"
                for="description"
                >Description</label
              >
            </div>
          </div>
        </div>

        <div class="row mt-2 mb-2">
          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.cult1"
                  class="form-control"
                  id="cult1"
                >
                  <option
                    v-for="option in cultOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <label
                  class="bb-float-label optional"
                  for="cult1"
                  >Cult 1</label
                >
              </div>
            </div>
          </div>

          <div class="col-6">
            <div class="form-group">
              <div class="form-floating">
                <select
                  v-model="local_spell.cult2"
                  class="form-control"
                  id="cult2"
                >
                  <option
                    v-for="option in cultOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <label
                  class="bb-float-label optional"
                  for="cult2"
                  >Cult 2</label
                >
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="errors.length > 0"
          class="row"
        >
          <div class="col text-end me-3">
            <div
              v-for="err in errors"
              :key="err"
              class="bb-error-wrap"
              style="white-space: pre-line"
            >
              {{ err }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'
import CounterEmit from './CounterEmit.vue'
import copyObjectNoRef from '../helpers/copyObjectNoRef'

const DEFAULT_SPELL = {
  name: '',
  type: 'Rune',
  casting_rune: null,
  secondary_rune: null,
  casting_time: '1 CA',
  duration: 'Instant',
  area: 'n/a',
  range: 'POW',
  resist: 'n/a',
  progressive: false,
  triggered: false,
  mag: 1,
  cult1: 'n/a',
  cult2: 'n/a',
  short_description: '',
  description: '',
}

function capitaliseInitial(value) {
  const name = (value ?? '').trim()
  if (!name) return ''
  return name.replace(/\b\S/g, (char) => char.toUpperCase())
}

function normaliseId(value) {
  return value === undefined || value === null ? '' : String(value)
}

export default {
  name: 'SpellStaticCard',
  emits: [
    'add-static-spell',
    'update-static-spell',
    'delete-static-spell',
    'close-static-spell-panel',
  ],
  components: {
    ConfirmDialog,
    CounterEmit,
  },
  props: {
    spell: {
      type: Object,
      default: null,
    },
    spells: {
      type: Array,
      default: () => [],
    },
    runes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      original_spell: {},
      local_spell: copyObjectNoRef(DEFAULT_SPELL),
      errors: [],
      errorFields: [],
      submitCommitted: false,
      spellTypes: ['Rune', 'Sorcery', 'Divine', 'Other'],
      castingTimeOptions: ['1 CA', '2 CA', '3 CA', '5 CA', 'Special'],
      durationOptions: [
        'Instant',
        '1 min',
        '5 mins',
        '10 mins',
        '15 mins',
        'D4+3 CA',
        'Concentration',
        'Permanent',
        'Special',
      ],
      areaOptions: [
        'n/a',
        '3m radius',
        '5m radius',
        '10m radius',
        '20m radius',
        '100m radius',
        'Special',
      ],
      rangeOptions: ['n/a', 'POW', 'Touch', 'Special'],
      resistOptions: ['n/a', 'Persistence', 'Resilience', 'Dodge'],
      cultOptions: [
        'n/a',
        'All',
        'Air',
        'Beast',
        'Chaos',
        'Death',
        'Earth',
        'Black Fang',
        'Fertility',
        'Hunter',
        'Moon',
        'Night',
        'Sea',
        'Storm',
        'Sun',
        'Trickster',
        'War',
        'Water',
      ],
    }
  },
  computed: {
    computeMagMin() {
      if (this.local_spell.progressive) return 1
      return 0
    },
    isNewSpell() {
      return !this.local_spell?.id
    },
    rowNumber() {
      return (this.local_spell?.originalIndex ?? 0) + 1
    },
    spellHeading() {
      return this.local_spell?.name || 'New Spell'
    },
    hasErrors() {
      return this.errors.length > 0
    },
    canSubmit() {
      return !this.hasErrors && (this.isNewSpell || this.isChanged)
    },
    isChanged() {
      return (
        JSON.stringify(this.buildPayload(this.original_spell)) !==
        JSON.stringify(this.buildPayload(this.local_spell))
      )
    },
    detailSelectFields() {
      return [
        {
          key: 'casting_time',
          label: 'Casting Time',
          options: this.castingTimeOptions,
          class: 'col-3',
        },
        {
          key: 'duration',
          label: 'Duration',
          options: this.durationOptions,
          class: 'col-5',
        },
        {
          key: 'range',
          label: 'Range',
          options: this.rangeOptions,
          class: 'col-3',
        },
      ]
    },
  },
  mounted() {
    this.resetLocalSpell()
  },
  watch: {
    local_spell: {
      deep: true,
      handler() {
        this.validate()
      },
    },
    spells() {
      this.validate()
    },
  },
  methods: {
    resetLocalSpell() {
      const source = this.spell
        ? { ...DEFAULT_SPELL, ...this.spell }
        : DEFAULT_SPELL
      source.name = capitaliseInitial(source.name)
      this.local_spell = copyObjectNoRef(source)
      this.original_spell = copyObjectNoRef(source)
      this.validate()
    },
    onNameInput() {
      const capitalisedName = capitaliseInitial(this.local_spell.name)
      if (this.local_spell.name !== capitalisedName) {
        this.local_spell.name = capitalisedName
      }
      this.validate()
    },
    onTypeChange() {
      if (this.local_spell.type !== 'Rune') {
        this.local_spell.casting_rune = null
        this.local_spell.secondary_rune = null
      }
      this.validate()
    },
    onProgressiveChange() {
      if (this.local_spell.progressive && Number(this.local_spell.mag) < 1) {
        this.local_spell.mag = 1
      }
      this.validate()
    },
    onCounterChanged({ ref, count }) {
      this.local_spell = this.local_spell || {}

      if (ref === 'mag') {
        this.local_spell.mag = count
        this.validate()
      }
    },
    findDuplicateSpell(name) {
      const candidateName = name.toLowerCase()
      const candidateType = this.local_spell.type || 'Rune'
      const candidateId = normaliseId(this.local_spell.id ?? this.spell?.id)
      const candidateCastingRune = normaliseId(this.local_spell.casting_rune)

      return this.spells.find((spell) => {
        if (normaliseId(spell?.id) === candidateId) {
          return false
        }

        if ((spell?.name ?? '').trim().toLowerCase() !== candidateName) {
          return false
        }

        if (candidateType === 'Rune') {
          return (
            spell?.type === 'Rune' &&
            normaliseId(spell?.casting_rune) === candidateCastingRune
          )
        }

        return spell?.type !== 'Rune'
      })
    },
    buildPayload(source) {
      const mag = Number(source?.mag)
      const type = source?.type || 'Rune'

      return {
        name: capitaliseInitial(source?.name),
        type,
        casting_rune: type === 'Rune' ? source?.casting_rune || null : null,
        secondary_rune: type === 'Rune' ? source?.secondary_rune || null : null,
        casting_time: source?.casting_time || '1 CA',
        duration: source?.duration || 'Instant',
        area: source?.area || 'n/a',
        range: source?.range || 'POW',
        resist: source?.resist || 'n/a',
        progressive: !!source?.progressive,
        triggered: !!source?.triggered,
        mag: Number.isFinite(mag) ? mag : 0,
        cult1: source?.cult1 || 'n/a',
        cult2: source?.cult2 || 'n/a',
        short_description: source?.short_description ?? '',
        description: source?.description ?? '',
      }
    },
    validate() {
      this.errors = []
      this.errorFields = []

      const name = (this.local_spell.name ?? '').trim()
      const duplicateSpell = name ? this.findDuplicateSpell(name) : null

      if (!name) {
        this.errors.push(window.bbUiErrorMsg('Spell name is required.'))
        this.errorFields.push('name')
      } else if (name.length < 3) {
        this.errors.push(
          window.bbUiErrorMsg('Spell name must be at least 3 characters.'),
        )
        this.errorFields.push('name')
      }

      if (duplicateSpell) {
        const message =
          this.local_spell.type === 'Rune'
            ? 'Spell name already exists for this casting rune.'
            : 'Spell name already exists.'

        this.errors.push(window.bbUiErrorMsg(message))
        this.errorFields.push('name')

        if (this.local_spell.type === 'Rune') {
          this.errorFields.push('casting_rune')
        }
      }

      if (this.local_spell.type === 'Rune' && !this.local_spell.casting_rune) {
        this.errors.push(
          window.bbUiErrorMsg('Casting rune is required for rune spells.'),
        )
        this.errorFields.push('casting_rune')
      }

      const mag = Number(this.local_spell.mag)
      if (!Number.isInteger(mag) || mag < 0 || mag > 10) {
        this.errors.push(window.bbUiErrorMsg('Mag must be between 0 and 10.'))
        this.errorFields.push('mag')
      }
    },
    async confirmDeletion() {
      this.$emit('delete-static-spell', this.local_spell)
    },
    onSubmit() {
      if (this.submitCommitted) {
        return
      }

      this.validate()
      if (this.hasErrors) {
        return
      }

      this.submitCommitted = true

      const payload = this.buildPayload(this.local_spell)

      if (this.isNewSpell) {
        this.$emit('add-static-spell', { payload })
      } else {
        this.$emit('update-static-spell', {
          id: this.local_spell.id ?? this.spell?.id,
          payload,
        })
      }

      this.closeSidePanel()
    },
    closeSidePanel() {
      this.$emit('close-static-spell-panel')
    },
  },
}
</script>

<style scoped>
.form-floating textarea.bb-static-spell-short-description-control {
  min-height: 6.5em !important;
  height: 6.5em !important;
}

.form-floating textarea.bb-static-spell-description-control {
  min-height: 16.5em !important;
  height: 16.5em !important;
}
</style>
